var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/* eslint-disable no-case-declarations */
import { types, onPatch, applyPatch, applySnapshot, getSnapshot } from 'mobx-state-tree';
import _ from 'lodash';
import { dateTimeNow } from '../../helpers/DateHelpers';
import { generateGuid } from '../../helpers/RandomGenerator';
var BaseModel = types.model({
    id: types.optional(types.identifier, function () { return generateGuid(); }),
    createdAt: types.optional(types.Date, dateTimeNow),
    updatedAt: types.optional(types.Date, dateTimeNow),
    deleted: types.optional(types.boolean, false),
    isEditing: types.optional(types.boolean, false),
    editingIsPaused: types.optional(types.boolean, false)
})
    .named('BaseModel')
    .volatile(function (_self) { return ({
    activePatch: undefined,
    activeReversePatch: undefined,
    activeOnPatchSubscription: undefined
}); })
    .views(function (self) { return ({
    get hasChanges() {
        if (!self.activePatch) {
            return false;
        }
        return self.activePatch.length > 0;
    }
}); })
    .actions(function (self) {
    // #region internal actions
    var endEditing = function () {
        if (self.activeOnPatchSubscription) {
            self.activeOnPatchSubscription();
        }
        self.activePatch = undefined;
        self.activeReversePatch = undefined;
        self.isEditing = false;
        self.editingIsPaused = false;
    };
    // #endregion
    return ({
        setUpdatedAt: function (updatedAt) {
            self.updatedAt = updatedAt;
        },
        // #region Transactions
        beginEditing: function () {
            var _this = this;
            self.isEditing = true;
            self.editingIsPaused = false;
            self.activeOnPatchSubscription = onPatch(self, function (patch, reversePatch) {
                _this.handlePatch(patch, reversePatch);
            });
        },
        handlePatch: function (patch, reversePatch) {
            if (self.editingIsPaused) {
                return;
            }
            if (patch.path.endsWith('/isEditing') || patch.path.endsWith('/editingIsPaused')) {
                return;
            }
            if (!self.activePatch) {
                self.activePatch = [];
            }
            if (!self.activeReversePatch) {
                self.activeReversePatch = [];
            }
            // #region modify patch
            var lastSlashIndex = patch.path.lastIndexOf('/');
            switch (patch.op) {
                case 'add':
                    patch = __assign(__assign({}, patch), { path: patch.path.substring(0, lastSlashIndex) + "/-" });
                    break;
                case 'remove':
                    var removedObjectIndex = parseInt(patch.path.substring(lastSlashIndex + 1));
                    var removedObject = reversePatch.value;
                    var pathEnd = void 0;
                    if (removedObject.id) {
                        pathEnd = removedObject.id;
                    }
                    else {
                        pathEnd = removedObjectIndex;
                    }
                    patch = __assign(__assign({}, patch), { path: patch.path.substring(0, lastSlashIndex) + "/" + pathEnd, value: removedObject });
                    break;
                default:
                    break;
            }
            // #endregion
            // ignore all pathes in (self as any).patchIgnoreProperties
            if (self.patchIgnoreProperties && self.patchIgnoreProperties.includes(patch.path)) {
                return;
            }
            //#region replace array indexes in path with ID, if possible
            var pathParts = patch.path.split('/').filter(function (x) { return x != ''; });
            var optimizedPathParts = _.clone(pathParts); // we need an array clone with the modified path
            // ToDo: this algorithm doesn't support more arrays inside array items like file.version[0].propXy[1].name
            for (var i = 0; i < pathParts.length; i++) {
                var element = pathParts[i];
                var indexOfItem = parseInt(element);
                if (_.isInteger(indexOfItem)) {
                    var pathToArray = _.take(pathParts, i);
                    var array = _.property(pathToArray)(self);
                    if (_.isArray(array)) {
                        var item = array[indexOfItem];
                        if (item && item.id != undefined) {
                            optimizedPathParts[i] = item.id;
                        }
                    }
                }
            }
            patch = __assign(__assign({}, patch), { path: "/" + optimizedPathParts.join('/') });
            //#endregion
            console.log('PATCH', self, patch, reversePatch);
            // ToDo: replace index with ID
            // check if activePatch already contains an operation for this path
            var indexOfExistingPatchWithSamePath = self.activePatch.findIndex(function (x) { return x.path === patch.path; });
            if (patch.op === 'add' || patch.op === 'remove' || indexOfExistingPatchWithSamePath === -1) {
                self.activePatch.push(patch);
                self.activeReversePatch.push(reversePatch);
            }
            else {
                self.activePatch[indexOfExistingPatchWithSamePath] = patch;
            }
        },
        pauseEditing: function () {
            self.editingIsPaused = true;
        },
        continueEditing: function () {
            self.editingIsPaused = false;
        },
        discardChanges: function () {
            if (self.activeReversePatch) {
                applyPatch(self, self.activeReversePatch);
            }
            endEditing();
        },
        commitChanges: function () {
            var patch = self.activePatch;
            endEditing();
            self.updatedAt = dateTimeNow();
            return patch;
        },
        /**
         * Call this method when a new jsonPatch arrives for this model
         * @param jsonPatchDocument
         */
        applyPatch: function (jsonPatchDocument) {
            console.log(jsonPatchDocument);
            // ToDo: replace IDs with indexes
            // ToDo: LATER
            // ToDo: if some ID is not here => if OP === ADD
            // ToDo: ? Replace with - : OP === Remove
            // ToDo: ? Remove from patch, because not relevant : OP === replace (property has changed...)
            // ToDo: figure out how to handle this... LATER
            // ToDo: END LATER
            // ToDo: IMPORTANT: If you are in editing mode => no data should get lost
            // applyPatch(self, jsonPatch);
        },
        applySnapshot: function (snapshot) {
            // Lazy loading support => prevent changes to default values because the property is not part of the snapshot
            var selfSnapshot = getSnapshot(self);
            var snapshotKeys = Object.keys(snapshot);
            Object.keys(selfSnapshot).filter(function (x) { return !snapshotKeys.includes(x); }).forEach(function (notExistingKey) {
                snapshot[notExistingKey] = selfSnapshot[notExistingKey];
            });
            applySnapshot(self, snapshot);
            // ToDo: IMPORTANT: If you are in editing mode => no data should get lost
        }
        // #endregion
    });
}).postProcessSnapshot(function (snapshot) {
    delete snapshot.deleted;
    delete snapshot.isEditing;
    delete snapshot.editingIsPaused;
    return snapshot;
});
export default BaseModel;
;
;
//# sourceMappingURL=BaseModel.js.map